import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { AffectedAutomationInterface } from '@app/models/util';
import { BaseDialog } from '../BaseDialog/BaseDialog';
import { Button } from '../Button/Button';
import TrashIcon from '@app/assets/icons/trash.svg?react';
import CloseIcon from '@app/assets/icons/close.svg?react';

// Deleting a tag/list can leave an automation without filters, which would make it target all customers
export const AudienceDeleteDialog = ({
  open,
  onClose,
  noun,
  ids,
  names,
  customersCount,
  getImpact,
  deleteItems,
  onDeleted,
}: {
  open: boolean;
  onClose: () => void;
  noun: 'tag' | 'list';
  ids: string[];
  names: string[];
  customersCount: number;
  getImpact: (ids: number[]) => Promise<{ data: { automations: AffectedAutomationInterface[] } }>;
  deleteItems: (ids: number[], pauseAutomations: boolean) => Promise<unknown>;
  onDeleted: () => void;
}) => {
  const numericIds = ids.map(Number);
  const pluralNoun = ids.length === 1 ? noun : `${noun}s`;

  const { data: automations, isFetching, isError, refetch } = useQuery({
    queryKey: ['audience-delete-impact', noun, ids],
    queryFn: () => getImpact(numericIds).then((res) => res.data.automations),
    enabled: open && ids.length > 0,
    staleTime: 0,
    gcTime: 0,
  });
  const hasAffected = Boolean(automations?.length);

  const { mutate: submit, isPending } = useMutation({
    mutationFn: () => deleteItems(numericIds, hasAffected),
    onSuccess: () => {
      onDeleted();
      onClose();
    },
    onError: (err: any) => {
      toast.error(err?.data?.message ?? `Error deleting ${pluralNoun}`);
      // The set of affected automations could have changed since the dialog was opened
      refetch();
    },
  });

  return (
    <BaseDialog size="max-w-2xl" open={open} onClose={onClose}>
      <div className="border-b border-b-base-ghostWhite p-6 mb-6 flex gap-2 items-start justify-between">
        <div className="bg-base-antiFlash flex items-center p-1 rounded-lg">
          <TrashIcon width={38} height={38} />
        </div>
        <div className="mr-auto mt-2">
          <h5 className="font-bold text-lg leading-tight">Delete {pluralNoun}</h5>
        </div>
        <div>
          <button type="button" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
      </div>
      <div className="px-6 pb-4">
        <p className="text-base-davyGray text-sm">Are you sure you want to delete {ids.length === 1 ? `this ${noun}` : `these ${noun}s`}?</p>
        <p className="text-base-davyGray text-sm">
          <strong>{customersCount} customer(s)</strong> will be removed from {ids.length === 1 ? 'it' : 'them'}. The customers themselves are not deleted.
        </p>

        <ul className="my-4 max-h-[160px] overflow-y-auto space-y-1 text-sm font-medium">
          {names.map((name, index) => (
            <li key={`${name}-${index}`} className="break-all">
              {name}
            </li>
          ))}
        </ul>

        {isError && <p className="text-system-error text-sm mb-4">Could not check which automations use this {noun}. Close the dialog and try again.</p>}

        {hasAffected && (
          <div className="bg-base-seasalt border border-system-error rounded-lg p-4 mb-4 text-sm">
            <p className="font-bold mb-2">These automations would be left without tags and lists</p>
            <ul className="list-disc pl-5 mb-2 max-h-[160px] overflow-y-auto">
              {automations?.map((automation) => (
                <li key={automation.id} className="break-all">
                  {automation.name} <span className="text-base-coolGray">({automation.status.toLowerCase()})</span>
                </li>
              ))}
            </ul>
            <p className="text-base-davyGray">
              Active ones will be paused. An automation without tags and lists targets <strong>all customers</strong>, so starting it again will send requests to every new customer.
            </p>
          </div>
        )}

        <div className="flex items-center gap-4 justify-center">
          <Button kind="secondary" className="w-full" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button kind="danger" className="w-full" type="button" disabled={isPending || isFetching || isError} loading={isPending} onClick={() => submit()}>
            {hasAffected ? 'Delete and pause automations' : `Delete ${pluralNoun}`}
          </Button>
        </div>
      </div>
    </BaseDialog>
  );
};
